import { cn } from '@neo/lib/utils'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { downloadFile } from '@shared/utils'
import { DownloadIcon, FileIcon } from 'lucide-react'
import { useState } from 'react'
import { APPLICATION_FILE_EXTENSIONS } from 'v2/configs/constants'
import { truncateFileName } from 'v2/pages/conversations/utils'
import { ConvChannels } from 'v2/types/api/conversations'
import { AudioPlayer } from './audio-player'
import { toBrowserFileUrl } from './file-url'
import { type MediaProps, MIMETYPE } from './types'
import { WhatsAppMedia } from './whatsapp-media'

export const Media = ({
  file,
  multipleMedia,
  senderId,
  contactUuid,
  channel,
  message,
  hideMessage,
}: MediaProps) => {
  // Rewrite the docker-internal demo host to a browser-reachable one at the render
  // boundary (dev-only, env-gated; prod is identity) so console images/videos load.
  // Lazy initializer so the rewrite runs once on mount, not on every render.
  const [mediaUrl, setMediaUrl] = useState<string>(() => toBrowserFileUrl(file.file_url))
  const { file_name: fileName, file_type: fileType } = file
  const mimeType = fileType.split('/')[0] as MIMETYPE
  const fileExtension = fileName?.split('.').pop()?.toLowerCase()
  const isApplication =
    mimeType === MIMETYPE.APPLICATION ||
    mimeType === MIMETYPE.TEXT ||
    APPLICATION_FILE_EXTENSIONS.includes(fileExtension || '')
  const isAudio = mimeType === MIMETYPE.AUDIO
  const isWhatsApp = channel === ConvChannels.WHATSAPP

  const handleDownload = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    downloadFile(toBrowserFileUrl(file.file_url), fileName)
  }

  const renderContent = () => {
    if (!mediaUrl) return <Typography variant="S/400">Media not available</Typography>

    switch (mimeType) {
      case MIMETYPE.IMAGE:
        return (
          <div className="flex flex-col gap-2 min-h-5 h-full">
            <img
              src={mediaUrl}
              alt={fileName}
              className="max-w-full max-h-[300px] rounded object-cover h-full"
            />
            {!isWhatsApp && (
              <div className="font-jetbrains text-xs-400 self-start text-secondary text-left max-w-[90%]">
                {truncateFileName(fileName, multipleMedia ? 12 : 30)}
              </div>
            )}
          </div>
        )
      case MIMETYPE.VIDEO:
        return (
          <div className="flex flex-col gap-2 min-h-5">
            <video
              src={mediaUrl}
              controls
              className="max-w-full max-h-[300px] rounded"
              controlsList="nodownload"
            >
              <track kind="captions" src="" label="English" srcLang="en" />
            </video>
            {!isWhatsApp && (
              <div className="font-jetbrains text-xs-400 self-start text-secondary text-left max-w-[90%]">
                {truncateFileName(fileName, multipleMedia ? 12 : 30)}
              </div>
            )}
          </div>
        )
      case MIMETYPE.AUDIO:
        return <AudioPlayer url={mediaUrl} fileName={fileName} />
      default:
        return (
          <div className="flex gap-2 bg-background items-center p-1.5 rounded-lg text-left">
            <FileIcon size={20} />
            <Tooltip content="Download file">
              <Typography variant="XS/400" className="font-jetbrains break-all">
                {truncateFileName(fileName, multipleMedia ? 12 : undefined)}
              </Typography>
            </Tooltip>
            <Tooltip content="Download">
              <button
                type="button"
                className="shrink-0 p-1 bg-hv1 text-secondary rounded-lg ml-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={handleDownload}
                data-action="download"
                data-testid="button-download-attachment"
                aria-label={`Download ${fileName}`}
              >
                <DownloadIcon size={16} />
              </button>
            </Tooltip>
          </div>
        )
    }
  }

  return (
    <div
      className={cn(
        multipleMedia ? 'max-w-[200px]' : 'w-full max-w-md flex items-center',
        'relative group dark:[color-scheme:dark]',
      )}
    >
      {!mediaUrl && isWhatsApp ? (
        <WhatsAppMedia
          file={file}
          senderId={senderId}
          contactUuid={contactUuid}
          onMediaLoaded={setMediaUrl}
        />
      ) : (
        <div className="flex flex-col gap-2 w-full h-full">
          {renderContent()}
          {message && !hideMessage && (
            <Typography variant="P/400" className="text-left">
              {message}
            </Typography>
          )}
        </div>
      )}
      {!isApplication && !isAudio && mediaUrl && (
        <div className="opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 absolute top-4 right-4 flex gap-2">
          <Tooltip content="Download">
            <button
              type="button"
              className="p-1.5 border border-border bg-card text-foreground rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={handleDownload}
              data-action="download"
              data-testid="button-download-media"
              aria-label={`Download ${fileName}`}
            >
              <DownloadIcon size={16} />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  )
}
